/**
 * @file: This file holds the form of the takeover panel.
 *
 * The panel asks for six things: the bumper of the act, how many times it
 * plays, whether it takes the first slot, a media to remove, a name, and a
 * start and an end. This class holds those answers and says what is still
 * missing. It has no Angular dependency and it writes nothing.
 *
 * The reason to change this file is a change of the form. The writes are in the
 * component, and the order of the copy is in `ComposeState`.
 */

import moment from 'moment'

/** The number of bumper plays a takeover starts with, from the RF26 data. */
const TAKEOVER_COUNT = 5

class TakeoverPanelState {
  constructor () {
    this.bumper = null
    this.search = ''
    this.count = TAKEOVER_COUNT
    this.first = true
    this.replaceRef = ''
    this.name = ''
    // The name follows the bumper until the operator types one.
    this.nameTouched = false
    this.start = null
    this.end = null
  }

  /** Choose the bumper. The name follows the act until the operator types one. */
  selectBumper (media, suggestedName) {
    this.bumper = media

    if (!this.nameTouched) {
      this.name = suggestedName
    }
  }

  /** The operator typed a name, so it is theirs and no bumper changes it. */
  touchName () {
    this.nameTouched = true
  }

  stepCount (delta) {
    this.count = Math.max(1, this.count + delta)
  }

  trimmedName () {
    return (this.name || '').trim()
  }

  /**
   * What stops a takeover from being created, or null when nothing does.
   *
   * The button and the command read the same answer. Two writes make a
   * takeover, and the second can fail, so every reason to refuse is found
   * before the first one runs.
   *
   * @param {object} context - `hasScene` and `canCopy` from the page
   * @returns {string|null} The reason, or null
   */
  problem ({ hasScene, canCopy }) {
    if (!this.bumper) {
      return 'Choose the bumper of the act.'
    }

    if (!this.trimmedName()) {
      return 'The copy needs a name.'
    }

    if (!hasScene) {
      return 'This page has no scene, so the copy has nowhere to go.'
    }

    if (!canCopy) {
      return 'Remove the rows that cannot play first. The server refuses the copy as well.'
    }

    return this._timesProblem()
  }

  /** Both times, or neither, and the end after the start. */
  _timesProblem () {
    if (!this.start && !this.end) {
      return null
    }

    if (!this.start || !this.end) {
      return 'Give both a start and an end, or leave both empty.'
    }

    return moment(this.end).isAfter(moment(this.start))
      ? null
      : 'The end of the takeover must come after its start.'
  }

  /** Whether the operator asked for the copy to be scheduled at once. */
  isScheduled () {
    return Boolean(this.start && this.end)
  }

  /** What `ComposeState.takeoverRows` needs to build the copy. */
  rowOptions (screens) {
    return {
      count: this.count,
      first: this.first,
      replaceRef: this.replaceRef || null,
      screens
    }
  }
}

export { TakeoverPanelState }
